import { useRef, useState } from "react";
import { Upload, Link2, X, Image as ImageIcon, Video, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadMedia, mediaSrc, guessType } from "../lib/media";

export function MediaInput({ media = [], onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");

  const add = (item) => onChange([...(media || []), item]);
  const removeAt = (idx) => onChange(media.filter((_, i) => i !== idx));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadMedia(file);
      add({ type: res.type, url: res.url });
      toast.success("Media uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addUrl = () => {
    const v = url.trim();
    if (!v) return;
    add({ type: guessType(v), url: v });
    setUrl("");
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid="media-upload-btn"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 hover:bg-clinical/5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Upload image / video
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          onChange={onFile}
          className="hidden"
          data-testid="media-file-input"
        />
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="…or paste an image/video URL"
            data-testid="media-url-input"
            className="w-full border border-line bg-white rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:border-clinical"
          />
        </div>
        <button
          type="button"
          onClick={addUrl}
          data-testid="media-add-url-btn"
          className="text-sm font-semibold bg-clinical text-white rounded-md px-3 py-2 hover:bg-clinicaldark"
        >
          Add
        </button>
      </div>

      {media?.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="media-previews">
          {media.map((m, idx) => (
            <div key={m.url || idx} className="relative group border border-line rounded-md overflow-hidden bg-sand aspect-video">
              {m.type === "video" ? (
                <div className="w-full h-full flex items-center justify-center text-clinical">
                  <Video className="w-7 h-7" />
                </div>
              ) : (
                <img src={mediaSrc(m.url)} alt="" className="w-full h-full object-cover" />
              )}
              <span className="absolute top-1 left-1 bg-black/60 text-white rounded px-1.5 py-0.5 text-[10px] flex items-center gap-1">
                {m.type === "video" ? <Video className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />} {m.type}
              </span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                data-testid={`media-remove-${idx}`}
                className="absolute top-1 right-1 bg-critical text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
